import { useCallback, useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import { Notification } from "@/types/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { markNotificationAsRead } from "@/api/notifications";
import clsx from "clsx";
import { useRouteModal } from "@/hooks/use-route-modal";
import CardDetailsModal from "@/components/cards/CardDetails/CardDetailsModal";

interface Props {
  notification: Notification;
}

export function AlertsNotificationsItem({ notification }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isCardOpen, setIsCardOpen] = useRouteModal(
    `card-details-notification-${notification.id}`,
  );

  const message = notification.message;
  const createdAt = notification.created_at;
  const isRead = !!notification.read_at;

  const queryClient = useQueryClient();

  const createdAtFormatted = useMemo(() => {
    return format(createdAt, "MM/dd/yyyy hh:mm a");
  }, [createdAt]);

  const { mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: (id: string) => markNotificationAsRead(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const onMarkNotificationAsRead = useCallback(() => {
    if (isRead) {
      return;
    }

    if (isPending || isSuccess || isError) {
      return;
    }

    mutateAsync(notification.id);
  }, [isPending, isSuccess, isError, mutateAsync, notification.id, isRead]);

  const isClickable = useMemo(() => {
    return !!notification.card;
  }, [notification.card]);

  const onCardClick = useCallback(() => {
    if (!notification.card) {
      return;
    }
    setIsCardOpen(true);
  }, [notification.card, setIsCardOpen]);

  useEffect(() => {
    if (isRead) {
      return;
    }

    if (isSuccess || isPending || isError) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onMarkNotificationAsRead();
        }
      });
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [onMarkNotificationAsRead, isPending, isSuccess, isError, isRead]);

  return (
    <>
      <div
        className={clsx("grid gap-1", {
          "cursor-pointer": isClickable,
        })}
        onClick={onCardClick}
        ref={containerRef}
      >
        <p className="text-sm">{message}</p>
        <p className="text-xs text-muted-foreground">{createdAtFormatted}</p>
      </div>
      {notification.card && (
        <CardDetailsModal
          card={notification.card}
          isOpen={isCardOpen}
          onClose={() => setIsCardOpen(false)}
        />
      )}
    </>
  );
}
